// Universal search bar component
// Used on Dashboard and Archive pages
import { useEffect, useRef, useState } from 'react';
import { brand } from '@/lib/tokens';
import { Modal } from '@/components/ui/Modal';

export interface SearchFilters {
  search: string;
  date_from: string;
  date_to: string;
}

interface SearchBarProps {
  onSearch: (filters: SearchFilters) => void;
  placeholder?: string;
}

const EMPTY_FILTERS: SearchFilters = { search: '', date_from: '', date_to: '' };
const DEBOUNCE_MS = 300;

export function SearchBar({ onSearch, placeholder = 'Search by client email or subject...' }: SearchBarProps) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      onSearch({ search, date_from: dateFrom, date_to: dateTo });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFrom, dateTo]);

  function handleClear() {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    setSearch('');
    setDateFrom('');
    setDateTo('');
    onSearch(EMPTY_FILTERS);
  }

  const inputStyle = {
    background: 'rgba(var(--fg-rgb),0.05)',
    border: '1px solid rgba(var(--fg-rgb),0.12)',
    borderRadius: 6,
    padding: '8px 12px',
    color: 'var(--text-primary, #ffffff)',
    fontFamily: 'Manrope, sans-serif',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  };

  const labelStyle = {
    fontFamily: 'JetBrains Mono, monospace',
    textTransform: 'uppercase' as const,
    fontSize: 10,
    color: 'rgba(var(--fg-rgb),0.4)',
    letterSpacing: '0.06em',
    marginBottom: 4,
  };

  function focusAccent(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = brand.accent;
  }
  function blurDefault(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'rgba(var(--fg-rgb),0.12)';
  }

  const hasDateFilter = Boolean(dateFrom || dateTo);

  return (
    <div className="search-bar" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '12px 0', marginBottom: 16 }}>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        onFocus={focusAccent}
        onBlur={blurDefault}
      />

      {/* Desktop: FROM/TO always visible. Hidden on mobile via CSS. */}
      <div className="search-bar-date search-bar-date-desktop">
        <div style={labelStyle}>From</div>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={inputStyle}
          onFocus={focusAccent}
          onBlur={blurDefault}
        />
      </div>
      <div className="search-bar-date search-bar-date-desktop">
        <div style={labelStyle}>To</div>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={inputStyle}
          onFocus={focusAccent}
          onBlur={blurDefault}
        />
      </div>

      {/* Mobile: single dropdown trigger instead of inline FROM/TO fields. */}
      <button
        type="button"
        onClick={() => setFilterOpen(true)}
        className="search-bar-filter-trigger noir-button"
        style={{ flexShrink: 0 }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden>
          tune
        </span>
        {hasDateFilter ? 'Date filter active' : 'Filter'}
      </button>

      <button
        onClick={handleClear}
        className="search-bar-clear"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#68BBCB',
          fontSize: 12,
          fontFamily: 'Manrope, sans-serif',
          cursor: 'pointer',
          padding: '8px 4px',
          whiteSpace: 'nowrap',
        }}
      >
        Clear
      </button>

      <Modal
        open={filterOpen}
        title="Filter by Date"
        body={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={labelStyle}>From</div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <div style={labelStyle}>To</div>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={inputStyle}
              />
            </div>
            {hasDateFilter && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 'none',
                  color: brand.accent,
                  fontSize: 12,
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Clear date filter
              </button>
            )}
          </div>
        }
        confirmLabel="Apply"
        cancelLabel="Close"
        onConfirm={() => setFilterOpen(false)}
        onCancel={() => setFilterOpen(false)}
      />
    </div>
  );
}
